import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin health-check page — pings every integration in parallel via /api/admin/health-check
 * and shows a status tile per integration so the coach can see at a glance if anything is
 * broken (Stripe / Resend / ElevenLabs / Emergent LLM / MongoDB) without checking logs.
 */
export default function AdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { data } = await api.get("/admin/health-check");
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Health check failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-health-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">System health</p>
          <h1 className="font-display uppercase text-4xl text-white tracking-tight">Integration status</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-lg">
            Pings every external service (Stripe, Resend, ElevenLabs, Emergent LLM, MongoDB) in real time. Run this whenever something feels off.
          </p>
        </div>
        <Button
          onClick={() => load(false)} disabled={loading || refreshing}
          className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs"
          data-testid="health-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${(loading || refreshing) ? "animate-spin" : ""}`} />
          Run check
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Pinging services…
        </div>
      ) : !data ? null : (
        <>
          <OverallBanner overall={data.overall} checkedAt={data.checked_at} />

          <div className="grid md:grid-cols-2 gap-4">
            {(data.integrations || []).map((i) => (
              <IntegrationTile key={i.name} integration={i} />
            ))}
          </div>

          <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm p-4 text-xs text-zinc-500">
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold mb-2">Environment</p>
            <div className="grid sm:grid-cols-2 gap-y-1 font-mono">
              <span>Public URL: <span className="text-white">{data.env?.public_site_url || "—"}</span></span>
              <span>Mode: <span className="text-white">{data.env?.node_env || "—"}</span></span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Components ────────────────────────────────────────────────────

function OverallBanner({ overall, checkedAt }) {
  const meta = {
    ok:       { color: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400", icon: CheckCircle2, label: "All systems operational" },
    degraded: { color: "border-amber-500/40 bg-amber-500/10 text-amber-400",       icon: AlertTriangle, label: "Degraded — some keys missing" },
    fail:     { color: "border-red-500/40 bg-red-500/10 text-red-400",             icon: XCircle, label: "Failures detected — investigate below" },
  }[overall] || { color: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400", icon: AlertTriangle, label: overall };

  return (
    <div className={`border ${meta.color} rounded-sm p-5 flex items-center gap-4`} data-testid={`health-overall-${overall}`}>
      <meta.icon className="h-8 w-8 shrink-0" />
      <div className="flex-1">
        <div className="font-display uppercase text-xl tracking-tight">{meta.label}</div>
        <div className="text-xs opacity-70 mt-1 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Checked {new Date(checkedAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function IntegrationTile({ integration }) {
  const meta = {
    ok:      { color: "border-emerald-500/40", text: "text-emerald-400", icon: CheckCircle2, label: "Healthy" },
    fail:    { color: "border-red-500/40",     text: "text-red-400",     icon: XCircle,      label: "Failing" },
    missing: { color: "border-amber-500/40",   text: "text-amber-400",   icon: AlertTriangle, label: "Not configured" },
  }[integration.status] || { color: "border-zinc-500/40", text: "text-zinc-400", icon: Activity, label: integration.status };

  return (
    <div className={`border ${meta.color} bg-[#0A0A0A] rounded-sm p-4`} data-testid={`health-tile-${integration.name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display uppercase text-lg text-white tracking-tight">{integration.name}</h3>
        <span className={`inline-flex items-center gap-1 ${meta.text} text-[10px] uppercase tracking-widest font-bold`}>
          <meta.icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
      </div>
      {integration.latency_ms !== undefined && (
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
          {integration.latency_ms}ms response
        </div>
      )}
      <p className="text-xs text-zinc-400 leading-relaxed font-mono break-words">
        {integration.detail || "No detail."}
      </p>
    </div>
  );
}
